// components/AssetManager/MetadataFilterPanel.jsx
//
// Compact, Bynder-style filter bar (Type + Tags) – consistent UX:
// - Both fields are Autocomplete (same hover + type-to-filter behavior)
// - Type: multi-select with checkboxes (disableCloseOnSelect)
// - Tags: multi-select (chips shown ONLY under the bar; none inside fields)
// - NO underlines
// - Field label behavior:
//     • Empty & idle  → darker gray (not disabled-looking)
//     • Focused       → lighter “hint” gray while typing
//     • 1 selected    → show the selected VALUE (bold, label color)
//     • 2+ selected   → “N selected”
// - Field width = ~25% wider than static label (clamped)
// - Menu width = longest option (clamped), gapless Paper/List paddings
// - Applied chips row sits below the controls (smaller, condensed)
//
// State contract:
// filters: {
//   fileType?: string,       // legacy single (kept in sync from fileTypes[0])
//   fileTypes?: string[],    // new multi-select array for Type
//   tags?: string[]
// }

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Stack,
  Chip,
  Autocomplete,
  TextField,
  Checkbox,
  Divider,
  Button,
} from '@mui/material';

/* ── Design tokens ─────────────────────────────────────────────── */
const OPT_FONT_PX    = 12;                                 // input/options
const LABEL_FONT_WT  = 600;                                // label weight
const LABEL_COLOR    = (t) => t.palette.grey[800];         // active label/value color
const HINT_DARK      = (t) => t.palette.grey[700];         // empty/idle
const HINT_LIGHT     = (t) => t.palette.grey[500];         // focused hint
const CHIP_FONT_PX   = 10;                                 // chip text
const CHIP_HEIGHT    = 20;
const FIELD_PAD_PX   = 24;                                 // label→field width
const MENU_PAD_PX    = 24;                                 // option→menu width
const STACK_SPACING  = 0.5;                                // dense row
const MENU_MAX       = 240;                                // clamp menus

// Field clamps
const TYPE_FIELD_MIN = 110;
const TYPE_FIELD_MAX = 160;
const TAGS_FIELD_MIN = 110;
const TAGS_FIELD_MAX = 150;

/* ── Text measurement (for width calculation) ───────────────────── */
const measureTextPx = (text, font = '12px Roboto, Arial, sans-serif') => {
  if (typeof document === 'undefined') return text.length * 7;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text.length * 7;
  ctx.font = font;
  return ctx.measureText(text).width;
};

/* ── Gapless Autocomplete Menus (uniform 12px incl. “No options”) ─ */
const makeAutocompleteSlotProps = (menuWidthPx) => ({
  popper: {
    sx: {
      '& .MuiAutocomplete-paper': {
        width: `${menuWidthPx}px`,
        minWidth: `${menuWidthPx}px`,
        maxWidth: `${menuWidthPx}px`,
        margin: 0,
      },
    },
  },
  paper: { sx: { margin: 0, mt: 0, p: 0 } },
  listbox: {
    sx: {
      p: 0,
      '& .MuiAutocomplete-option': {
        fontSize: OPT_FONT_PX,
        lineHeight: 1.4,
        minHeight: 'auto',
        py: 0.25,
        px: 1,
      },
      '& .MuiAutocomplete-option.Mui-focused': { fontSize: OPT_FONT_PX },
      '& .MuiAutocomplete-option[aria-selected="true"]': { fontSize: OPT_FONT_PX },
      '& .MuiAutocomplete-noOptions': {
        fontSize: OPT_FONT_PX,
        lineHeight: 1.4,
        py: 0.25,
        px: 1,
      },
    },
  },
});

/* ── Width helpers ─────────────────────────────────────────────── */
const computeFieldWidth = (staticLabel, min, max) => {
  const px = measureTextPx(staticLabel);
  const padded = Math.ceil(px * 1.25) + FIELD_PAD_PX; // ~25% wider + pad
  return Math.max(min, Math.min(padded, max));
};
const computeMenuWidth = (options, fallback, fieldPx) => {
  const arr = Array.isArray(options) ? options : [];

  const longest =
    arr.reduce((acc, cur) => {
      const curStr = cur == null ? "" : String(cur);
      const accStr = acc == null ? "" : String(acc);
      return curStr.length > accStr.length ? curStr : accStr;
    }, fallback) || fallback;

  const px = measureTextPx(longest);
  const contentPx = Math.ceil(px) + MENU_PAD_PX;
  return Math.max(fieldPx, Math.min(contentPx, MENU_MAX));
};


/* ── Shared field styling (no underline, hover) ─────────────────── */
const baseFieldSx = {
  '& .MuiInputBase-root:before, & .MuiInputBase-root:after, & .MuiInputBase-root:hover:before':
    { borderBottom: '0 !important' },
  '& .MuiInputBase-root': {
    py: 0,
    '&:hover': {
      backgroundColor: (t) => t.palette.action.hover,
      borderRadius: 1,
    },
  },
  '& .MuiInputBase-input': {
    fontSize: OPT_FONT_PX,
    fontWeight: LABEL_FONT_WT,
    lineHeight: 1.4,
    py: 0.25,
    textAlign: 'center',
    cursor: 'pointer',
  },

  /* ▼ NEW: keep the arrow visible/clickable, but remove its own hover */
  '& .MuiAutocomplete-popupIndicator': {
    p: 0.25,
    mr: 0.25,
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },

  /* ✕ NEW (optional): if a clear “x” shows, suppress its hover too
     + shrink the icon so it matches the chevron size */
  '& .MuiAutocomplete-clearIndicator': {
    p: 0.25,
    mr: 0.25,
    backgroundColor: 'transparent !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '& .MuiSvgIcon-root': {
      fontSize: 14,   // ↓ reduce the X size
      width: 14,
      height: 14,
    },
  },
};



/* ───────────────────────────────── Component ───────────────────── */
const MetadataFilterPanel = ({
  filters,
  setFilters,
  availableTags = [],
  availableFileTypes = ['icon','banner','screenshot'],
  hideLegacyType = false,
  promotedFacets = { keys: [], arrayKeys: [], dateKeys: [] },
  promotedValues = { equals: {}, equalsAny: {}, arrayAny: {}, dateRange: {} },
  onPromotedChange = () => {},
  promotedLabels = {},   // UFI → human label
  promotedChoices = {},  // UFI → choices[] ({ value: optionId, label }) when provided
}) => {
//logs
    // DEBUG: what did we receive?
  console.log('[MFP] received facets:', {
    keys      : (promotedFacets?.keys || []).length,
    arrayKeys : (promotedFacets?.arrayKeys || []).length,
    dateKeys  : (promotedFacets?.dateKeys || []).length,
    sampleKeys: (promotedFacets?.keys || []).slice(0, 3),
  });
  console.log('[MFP] promotedLabels keys:', Object.keys(promotedLabels || {}).slice(0, 10));

  // Type supports multi-select via fileTypes[]; keep legacy fileType in sync
  // Memoize to stabilize reference for downstream useMemo deps.
  const fileTypes = useMemo(() => {
    return Array.isArray(filters?.fileTypes)
      ? filters.fileTypes
      : (filters?.fileType ? [filters.fileType] : []);
  }, [filters?.fileTypes, filters?.fileType]);

  const { tags = [] } = filters || {};

  /* Sizing from static labels (“Type”, “Tags”) */
  const { typeFieldPx, typeMenuPx } = useMemo(() => {
    const fieldPx = computeFieldWidth('Type', TYPE_FIELD_MIN, TYPE_FIELD_MAX);
    const menuPx = computeMenuWidth(
      availableFileTypes?.map((s) => s.charAt(0).toUpperCase() + s.slice(1)),
      'All',
      fieldPx
    );
    return { typeFieldPx: fieldPx, typeMenuPx: menuPx };
  }, [availableFileTypes]);

  const { tagsFieldPx, tagsMenuPx } = useMemo(() => {
    const fieldPx = computeFieldWidth('Tags', TAGS_FIELD_MIN, TAGS_FIELD_MAX);
    const menuPx = computeMenuWidth(availableTags, 'No options', fieldPx);
    return { tagsFieldPx: fieldPx, tagsMenuPx: menuPx };
  }, [availableTags]);

  const setPatch = (patch) => setFilters({ ...(filters || {}), ...patch });

// Friendly label for a facet UFI
const labelFor = (ufi) => promotedLabels?.[ufi] || ufi;

// NEW: resolve human label for a given choice value (optionId)
const valueLabelFor = (ufi, value) => {
  const choices = promotedChoices?.[ufi];
  if (!Array.isArray(choices)) return value;

  // New shape: [{ value, label }]
  if (choices.length && typeof choices[0] === 'object' && choices[0] !== null) {
    const hit = choices.find((c) => c?.value === value);
    return hit?.label || value;
  }

  // Legacy shape: string[] (label-only)
  // In that legacy case, we can only display the raw string.
  return value;
};

  /* Applied chips — smaller; show ONLY value; tooltip adds facet context
     Extended to include AUF facets: equals / equalsAny / arrayAny / dateRange */
  const appliedChips = useMemo(() => {
    const chips = [];

    // Hard-coded controls
   if (!hideLegacyType) {
     (fileTypes || []).forEach((t) =>
       chips.push({
         k: 'fileTypes',
         v: t,
         label: t,
         title: `Type: ${t}`,
       }),
     );
   }
    (tags || []).forEach((t) =>
      chips.push({
        k: 'tags',
        v: t,
        label: t,
        title: `Tag: ${t}`,
      }),
    );

    // AUF facets — equalsAny (multi)
Object.entries(promotedValues.equalsAny || {}).forEach(([ufi, arr]) => {
  const fieldLabel = promotedLabels?.[ufi] || ufi;
  (Array.isArray(arr) ? arr : []).forEach((val) => {
    if (!val) return;
    const label = valueLabelFor(ufi, val);
    chips.push({
      k: `eqAny:${ufi}`,      // type + ufi
      ufi,
      v: val,                 // canonical value (optionId)
      label,                  // human label for chip
      title: `${fieldLabel}: ${label}`,
    });
  });
});


    // AUF facets — equals (single)
    Object.entries(promotedValues.equals || {}).forEach(([ufi, val]) => {
      const fieldLabel = promotedLabels?.[ufi] || ufi;
      const s = (val == null ? '' : String(val)).trim();
      if (!s) return;
      chips.push({
        k: `eq:${ufi}`,
        ufi,
        v: s,
        label: s,
        title: `${fieldLabel}: ${s}`,
      });
    });

    // AUF facets — arrayAny (multi)
Object.entries(promotedValues.arrayAny || {}).forEach(([ufi, arr]) => {
  const fieldLabel = promotedLabels?.[ufi] || ufi;
  (Array.isArray(arr) ? arr : []).forEach((val) => {
    if (!val) return;
    const label = valueLabelFor(ufi, val);
    chips.push({
      k: `arr:${ufi}`,
      ufi,
      v: val,
      label,
      title: `${fieldLabel}: ${label}`,
    });
  });
});


    // AUF facets — dateRange (start / end)
    Object.entries(promotedValues.dateRange || {}).forEach(([ufi, rng]) => {
      const fieldLabel = promotedLabels?.[ufi] || ufi;
      if (rng && typeof rng === 'object') {
        if (rng.start) {
          chips.push({
            k: `date:${ufi}:start`,
            ufi,
            v: String(rng.start),
            label: `From ${rng.start}`,
            title: `${fieldLabel}: from ${rng.start}`,
          });
        }
        if (rng.end) {
          chips.push({
            k: `date:${ufi}:end`,
            ufi,
            v: String(rng.end),
            label: `To ${rng.end}`,
            title: `${fieldLabel}: to ${rng.end}`,
          });
        }
      }
    });

    return chips;
  }, [
    fileTypes,
    tags,
    promotedValues.equalsAny,
    promotedValues.equals,
    promotedValues.arrayAny,
    promotedValues.dateRange,
    promotedLabels,
    hideLegacyType,    
  ]);

  /* Remove a chip → update either hard-coded state or promotedValues */
  const removeChip = (k, v) => {
    // 1) hard-coded
    if (k === 'fileTypes') {
      const next = (fileTypes || []).filter((x) => x !== v);
      setPatch({ fileTypes: next, fileType: next[0] || '' });
      return;
    }
    if (k === 'tags') {
      setPatch({ tags: (tags || []).filter((x) => x !== v) });
      return;
    }

    // 2) AUF facets
    // k formats:
    //   eqAny:<ufi>
    //   eq:<ufi>
    //   arr:<ufi>
    //   date:<ufi>:start|end
    try {
      const parts = String(k).split(':'); // ['eqAny','<ufi>'] or ['date','<ufi>','start']
      const kind = parts[0];

      if (kind === 'eqAny') {
        const ufi = parts[1];
        const curr = promotedValues.equalsAny?.[ufi] || [];
        const next = (curr || []).filter((x) => x !== v);
        onPromotedChange({
          ...promotedValues,
          equalsAny: { ...(promotedValues.equalsAny || {}), [ufi]: next },
        });
        return;
      }

      if (kind === 'eq') {
        const ufi = parts[1];
        // clear single value
        const nextEq = { ...(promotedValues.equals || {}) };
        delete nextEq[ufi];
        onPromotedChange({ ...promotedValues, equals: nextEq });
        return;
      }

      if (kind === 'arr') {
        const ufi = parts[1];
        const curr = promotedValues.arrayAny?.[ufi] || [];
        const next = (curr || []).filter((x) => x !== v);
        onPromotedChange({
          ...promotedValues,
          arrayAny: { ...(promotedValues.arrayAny || {}), [ufi]: next },
        });
        return;
      }

      if (kind === 'date') {
        const ufi = parts[1];
        const which = parts[2]; // 'start' | 'end'
        const prev = promotedValues.dateRange?.[ufi] || {};
        const patch = { ...(prev || {}) };
        delete patch[which];

        // Remove empty objects entirely
        const nextDate = { ...(promotedValues.dateRange || {}) };
        if (Object.keys(patch).length > 0) {
          nextDate[ufi] = patch;
        } else {
          delete nextDate[ufi];
        }
        onPromotedChange({ ...promotedValues, dateRange: nextDate });
        return;
      }

      console.warn('[MFP] Unknown chip kind:', k);
    } catch (e) {
      console.warn('[MFP] Failed to remove chip (non-fatal):', e?.message || e);
    }
  };


  // ── Promoted filters handlers (equals / arrayAny / dateRange) ──
  const setEq = (ufi, val) => {
    onPromotedChange({
      ...promotedValues,
      equals: { ...(promotedValues.equals || {}), [ufi]: val || '' },
    });
  };

  // NEW: equalsAny (multi-select OR for "equals" facets with choices)
  const setEqAny = (ufi, arr) => {
    onPromotedChange({
      ...promotedValues,
      equalsAny: {
        ...(promotedValues.equalsAny || {}),
        [ufi]: Array.isArray(arr) ? arr : [],
      },
    });
  };

  const setArr = (ufi, arr) => {
    onPromotedChange({
      ...promotedValues,
      arrayAny: { ...(promotedValues.arrayAny || {}), [ufi]: arr || [] },
    });
  };
  const setDate = (ufi, patch) => {
    onPromotedChange({
      ...promotedValues,
      dateRange: {
        ...(promotedValues.dateRange || {}),
        [ufi]: { ...(promotedValues.dateRange?.[ufi] || {}), ...patch },
      },
    });
  };


  // Helpers to compute the content displayed in-field (value/summary/label)
  const getTypeDisplay = () => {
    const count = fileTypes.length;
    if (count === 0) return 'Type';
    if (count === 1) {
      // display the single selected value (capitalized) as label-colored text
      const raw = fileTypes[0];
      const label = raw.charAt(0).toUpperCase() + raw.slice(1);
      return label;
    }
    return `${count} selected`;
  };

  const getTagsDisplay = () => {
    const count = tags.length;
    if (count === 0) return 'Tags';
    if (count === 1) return tags[0]; // show the one tag selected
    return `${count} selected`;
  };

  // Dynamic per-field SX so 1-selected value uses label color (not hint)
  const fieldSx = (hasValueOne) => ({
    ...baseFieldSx,
    '& .MuiInputBase-input': {
      ...baseFieldSx['& .MuiInputBase-input'],
      color: (t) => (hasValueOne ? LABEL_COLOR(t) : LABEL_COLOR(t)),
    },
    // Placeholder color logic:
    //  - default/idle (not focused): darker gray hint
    //  - focused: lighter hint gray
    // When exactly 1 selected, we inject value via placeholder, but force its
    // color to label color by overriding ::placeholder color below.
    '& .MuiInputBase-input::placeholder': {
      color: HINT_DARK,
      opacity: 1,
    },
    '& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder': {
      color: HINT_LIGHT,
    },
    ...(hasValueOne
      ? {
          // force the placeholder (which is our displayed value) to label color
          '& .MuiInputBase-input::placeholder': {
            color: LABEL_COLOR,
            opacity: 1,
          },
          '& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder': {
            color: LABEL_COLOR,
          },
        }
      : {}),
  });

  return (
    <Box sx={{ mb: 0.5 }}>
    {/* Controls row (compact; identical UX for both fields) */}
    <Stack
      direction="row"
      spacing={STACK_SPACING}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >

       {/* TYPE — legacy control; hide when AUF Asset Type facet is in play */}
       {!hideLegacyType && (
         <Autocomplete
           multiple
           disableCloseOnSelect
           size="small"
           options={(availableFileTypes || []).map(
             (s) => s.charAt(0).toUpperCase() + s.slice(1)
           )}
           value={(fileTypes || []).map(
             (s) => s.charAt(0).toUpperCase() + s.slice(1)
           )}
           onChange={(_e, nextLabels) => {
             const toRaw = (s) => s.charAt(0).toLowerCase() + s.slice(1);
             const nextRaw = nextLabels.map(toRaw);
             setPatch({ fileTypes: nextRaw, fileType: nextRaw[0] || '' });
           }}
           slotProps={makeAutocompleteSlotProps(typeMenuPx)}
           sx={{
             flex: '0 0 auto',
             minWidth: `${typeFieldPx}px`,
             maxWidth: `${typeFieldPx}px`,
             ...fieldSx(fileTypes.length === 1),
           }}
           renderTags={() => null}
           noOptionsText={
             <Box sx={{ fontSize: OPT_FONT_PX, lineHeight: 1.4, py: 0.25, px: 1 }}>
               No options
             </Box>
           }
           renderOption={(props, option, { selected }) => {
             const { key, ...liProps } = props;
             return (
               <li
                 key={key}
                 {...liProps}
                 style={{
                   fontSize: OPT_FONT_PX,
                   lineHeight: 1.4,
                   minHeight: 'auto',
                   paddingTop: 2,
                   paddingBottom: 2,
                   paddingLeft: 6,
                   paddingRight: 6,
                   display: 'flex',
                   alignItems: 'center',
                   gap: 6,
                 }}
               >
                 <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
                 {option}
               </li>
             );
           }}
           renderInput={(params) => (
             <TextField
               {...params}
               variant="standard"
               placeholder={getTypeDisplay()}
               InputProps={{
                 ...params.InputProps,
                 disableUnderline: true,
               }}
               inputProps={{
                 ...params.inputProps,
                 'aria-label': 'Select types',
                 style: {
                   textAlign: 'center',
                   fontWeight: LABEL_FONT_WT,
                 },
               }}
             />
           )}
         />
       )}


        {/* TAGS — Autocomplete (multiple) */}
        <Autocomplete
          multiple
          size="small"
          options={availableTags || []}
          value={tags || []}
          onChange={(_e, next) => setPatch({ tags: next })}
          limitTags={2}
          slotProps={makeAutocompleteSlotProps(tagsMenuPx)}
          sx={{
            flex: '0 0 auto',
            minWidth: `${TAGS_FIELD_MIN}px`,
            maxWidth: `${tagsFieldPx}px`,
            ...fieldSx(tags.length === 1),
          }}
          // Suppress chips INSIDE the field (we show them only below)
          renderTags={() => null}
          noOptionsText={
            <Box sx={{ fontSize: OPT_FONT_PX, lineHeight: 1.4, py: 0.25, px: 1 }}>
              No options
            </Box>
          }
          renderOption={(props, option) => {
            const { key, ...liProps } = props; // ⬅️ extract key
            return (
              <li
                key={key}
                {...liProps}
                style={{
                  fontSize: OPT_FONT_PX,
                  lineHeight: 1.4,
                  minHeight: 'auto',
                  paddingTop: 2,
                  paddingBottom: 2,
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                {option}
              </li>
            );
          }}

          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              placeholder={getTagsDisplay()}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
              }}
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select tags',
                style: {
                  textAlign: 'center',
                  fontWeight: LABEL_FONT_WT,
                },
              }}
            />
          )}
        />

       {/* Equals (string) facets — dropdown when choices exist; else text field */}
       {(promotedFacets.keys || [])
         .filter((ufi) => promotedLabels[ufi]) // wait until we have a label
         .map((ufi) => {
           // DEBUG per-equals facet (keep your log)
           console.log('[MFP] eq facet render:', {
             ufi,
             label: promotedLabels[ufi] || ufi
           });

const choices = Array.isArray(promotedChoices?.[ufi])
  ? promotedChoices[ufi].filter(Boolean)
  : [];
const hasChoices =
  choices.length > 0 &&
  typeof choices[0] === 'object' &&
  choices[0] !== null &&
  'value' in choices[0];

const current = promotedValues.equals?.[ufi] || '';
const placeholder = labelFor(ufi);


// If we have catalog choices → MULTI-select Autocomplete (OR semantics)
if (hasChoices) {
  // canonical values (optionIds) currently selected
  const selectedValues = Array.isArray(promotedValues.equalsAny?.[ufi])
    ? promotedValues.equalsAny[ufi]
    : [];

  // rehydrate Autocomplete value as full {value,label} objects
  const selectedOptions = choices.filter((opt) =>
    selectedValues.includes(opt.value)
  );

  // Same display rules as Type/Tags:
  //   0 → label, 1 → show value, 2+ → “N selected”
  const display =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
        ? (selectedOptions[0]?.label || placeholder)
        : `${selectedValues.length} selected`;

console.log('[MFP][CHOICE_FILTER]', {
  ufi,
  fieldLabel: promotedLabels?.[ufi],
  selectedValues,              // <-- should be optionIds
  selectedOptions,             // <-- {value,label}
  rawPromotedValues: promotedValues.equalsAny?.[ufi],
});

  return (
    <Autocomplete
      key={`eq-${ufi}`}
      multiple
      disableCloseOnSelect
      size="small"
      options={choices}
      value={selectedOptions}
      onChange={(_e, nextOptions) =>
        setEqAny(
          ufi,
          Array.isArray(nextOptions)
            ? nextOptions
                .map((opt) => opt?.value)
                .filter(Boolean)
            : []
        )
      }

      // keep menu width + paddings consistent with Type
      slotProps={makeAutocompleteSlotProps(
      computeMenuWidth(
        choices.map((c) => c?.label).filter(Boolean),
        'No options',
        typeFieldPx
      )

      )}
      sx={{
        flex: '0 0 auto',
        minWidth: `${TYPE_FIELD_MIN}px`,
        maxWidth: `${TYPE_FIELD_MAX}px`,
        ...fieldSx(selectedValues.length === 1),
      }}
      // 🔴 keep the field clean (chips only in the chips row below)
      renderTags={() => null}
      // render checkboxes like the Type field
      renderOption={(props, option, { selected }) => {
        const { key, ...liProps } = props; // ⬅️ extract key
        return (
          <li
            key={key}
            {...liProps}
            style={{
              fontSize: OPT_FONT_PX,
              lineHeight: 1.4,
              minHeight: 'auto',
              paddingTop: 2,
              paddingBottom: 2,
              paddingLeft: 6,
              paddingRight: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
            {option.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          placeholder={display}
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
          }}
          inputProps={{
            ...params.inputProps,
            'aria-label': placeholder,
            style: {
              textAlign: 'center',
              fontWeight: LABEL_FONT_WT,
            },
          }}
        />
      )}
    />
  );
}





           // Fallback: no choices → free-text
           return (
             <TextField
               key={`eq-${ufi}`}
               size="small"
               variant="standard"
              placeholder={placeholder}
              value={current}
              onChange={(e) => setEq(ufi, e.target.value)}
               InputProps={{ disableUnderline: true }}
               sx={{
                 flex: '0 0 auto',
                 minWidth: `${TYPE_FIELD_MIN}px`,
                 maxWidth: `${TYPE_FIELD_MAX}px`,
                ...fieldSx(Boolean(current)),
               }}
             />
           );
         })}

       {/* Array (contains-any) facets — multi select when choices exist; else freeSolo */}
       {(promotedFacets.arrayKeys || []).map((ufi) => {
         // DEBUG log (keep)
         console.log('[MFP] array facet render:', {
           ufi,
           label: promotedLabels[ufi] || ufi
         });

        const choices = Array.isArray(promotedChoices?.[ufi])
          ? promotedChoices[ufi].filter(Boolean)
          : [];
        const hasChoices =
          choices.length > 0 &&
          typeof choices[0] === 'object' &&
          choices[0] !== null &&
          'value' in choices[0];

        const selectedValues = Array.isArray(promotedValues.arrayAny?.[ufi])
          ? promotedValues.arrayAny[ufi]
          : [];
        const placeholder = labelFor(ufi);

        // Rehydrate selection into full option objects when we have choices;
        // otherwise, treat them as freeSolo strings.
        const selectedOptions = hasChoices
          ? choices.filter((opt) => selectedValues.includes(opt.value))
          : selectedValues;

return (
  <Autocomplete
    key={`arr-${ufi}`}
    multiple
    size="small"
    freeSolo={!hasChoices}
    options={hasChoices ? choices : []}
    value={selectedOptions}
    onChange={(_e, next) => {
      if (!hasChoices) {
        // freeSolo path: next is array of strings
        setArr(ufi, next || []);
        return;
      }
      const values = Array.isArray(next)
        ? next.map((opt) => opt?.value).filter(Boolean)
        : [];
      setArr(ufi, values);

    }}
    slotProps={makeAutocompleteSlotProps(tagsMenuPx)}
    disableCloseOnSelect={hasChoices} // only needed when showing options
    sx={{
      flex: '0 0 auto',
      minWidth: `${TAGS_FIELD_MIN}px`,
      maxWidth: `${tagsFieldPx}px`,
      ...fieldSx(selectedValues.length === 1),
    }}
    // suppress chips inside the field (we show them below)
    renderTags={() => null}
    noOptionsText={
      <Box sx={{ fontSize: OPT_FONT_PX, lineHeight: 1.4, py: 0.25, px: 1 }}>
        No options
      </Box>
    }
    // When we have choices, show checkboxes like the Type control
renderOption={(props, option, state) => {
  // ⬅️ Pull 'key' off so we don’t spread it
  const { key, ...liProps } = props;

  if (hasChoices) {
    return (
      <li
        key={key}
        {...liProps}
        style={{
          fontSize: OPT_FONT_PX,
          lineHeight: 1.4,
          minHeight: 'auto',
          paddingTop: 2,
          paddingBottom: 2,
          paddingLeft: 6,
          paddingRight: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Checkbox size="small" checked={state.selected} sx={{ p: 0.25 }} />
        {option.label}
      </li>
    );
  }

  // freeSolo path (no catalog choices)
  return (
    <li
      key={key}
      {...liProps}
      style={{
        fontSize: OPT_FONT_PX,
        lineHeight: 1.4,
        minHeight: 'auto',
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 8,
        paddingRight: 8,
      }}
    >
      {option}
    </li>
  );
}}


    renderInput={(params) => (
      <TextField
        {...params}
        variant="standard"
        placeholder={placeholder}
        InputProps={{ ...params.InputProps, disableUnderline: true }}
        inputProps={{
          ...params.inputProps,
          'aria-label': `${placeholder} (contains any)`,
          style: { textAlign: 'center', fontWeight: LABEL_FONT_WT },
        }}
      />
    )}
  />
);

       })}



       {/* Date range facets — two compact TextFields side-by-side */}
       {(promotedFacets.dateKeys || []).map((ufi) => (
        console.log('[MFP] date facet render:', { ufi, label: promotedLabels[ufi] || ufi }),

         <Stack key={`dt-${ufi}`} direction="row" spacing={0.5} alignItems="center">
           <TextField
             size="small"
             variant="standard"
             placeholder={`${promotedLabels[ufi] || ufi} from`}
             value={promotedValues.dateRange?.[ufi]?.start || ''}
             onChange={(e) => setDate(ufi, { start: e.target.value })}
             InputProps={{ disableUnderline: true }}
             sx={{
               flex: '0 0 auto',
               minWidth: 140,
               ...fieldSx(Boolean(promotedValues.dateRange?.[ufi]?.start)),
             }}
           />
           <TextField
             size="small"
             variant="standard"
             placeholder={`${promotedLabels[ufi] || ufi} to`}
             value={promotedValues.dateRange?.[ufi]?.end || ''}
             onChange={(e) => setDate(ufi, { end: e.target.value })}
             InputProps={{ disableUnderline: true }}
             sx={{
               flex: '0 0 auto',
               minWidth: 140,
               ...fieldSx(Boolean(promotedValues.dateRange?.[ufi]?.end)),
             }}
           />
         </Stack>
       ))}
      </Stack>


      {/* Applied chips row (SMALL, BELOW the filter bar only) */}
      {(fileTypes.length > 0 || tags.length > 0 || appliedChips.length > 0) && (
        <>
          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            flexWrap="wrap"
            sx={{ mt: 0.5, mb: 0.5 }}
          >
            {appliedChips.map((c) => (
              <Chip
                key={`${c.k}:${c.v}`}
                label={c.label}
                title={c.title}
                size="small"
                variant="outlined"
                sx={{ height: CHIP_HEIGHT, fontSize: CHIP_FONT_PX }}
                onDelete={() => removeChip(c.k, c.v)}
              />
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <Button
              size="small"
              variant="text"
              onClick={() => {
                // Clear hard-coded
                setPatch({ fileTypes: [], fileType: '', tags: [] });
                // Clear all AUF filters
                onPromotedChange({
                  equals: {},
                  equalsAny: {},
                  arrayAny: {},
                  dateRange: {},
                });
              }}
              sx={{ minWidth: 0, px: 1, fontSize: 11 }}
            >
              Clear
            </Button>
          </Stack>
          <Divider />
        </>
      )}
    </Box>
  );
};

MetadataFilterPanel.propTypes = {
  filters: PropTypes.shape({
    fileType: PropTypes.string,                  // legacy single (kept in sync)
    fileTypes: PropTypes.arrayOf(PropTypes.string), // new multi
    tags: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  availableTags: PropTypes.arrayOf(PropTypes.string),
  availableFileTypes: PropTypes.arrayOf(PropTypes.string),
  hideLegacyType: PropTypes.bool,
  promotedFacets: PropTypes.shape({
    keys: PropTypes.arrayOf(PropTypes.string),
    arrayKeys: PropTypes.arrayOf(PropTypes.string),
    dateKeys: PropTypes.arrayOf(PropTypes.string),
  }),
  promotedValues: PropTypes.shape({
    equals: PropTypes.object,
   equalsAny: PropTypes.object,      // { [ufi]: string[] }
    arrayAny: PropTypes.object,
    dateRange: PropTypes.object,
  }),
  onPromotedChange: PropTypes.func,
  promotedLabels: PropTypes.object,
  promotedChoices: PropTypes.object,
};



export default MetadataFilterPanel;
